import { useEffect, useMemo, useRef, useState } from "react";
import { Check, ChevronDown, Search, X } from "lucide-react";
import { AttendanceEmployee } from "../api/attendanceApi";

interface Props {
  employees: AttendanceEmployee[];
  selectedCodes: string[];
  onChange: (codes: string[]) => void;
  placeholder?: string;
  className?: string;
}

/** Searchable multi-select filter for picking employees on the Attendance
 * page — unlike shared/MultiAssignDropdown (a one-shot "assign and clear"
 * action), selections here persist and apply live via onChange, matching
 * the page's existing date/status filters. */
export default function EmployeeMultiSelect({
  employees, selectedCodes, onChange, placeholder = "Search employees…", className = "",
}: Props) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onOutside(e: MouseEvent) {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onOutside);
    return () => document.removeEventListener("mousedown", onOutside);
  }, [open]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return employees;
    return employees.filter((e) => e.name.toLowerCase().includes(q) || e.employee_code.toLowerCase().includes(q));
  }, [employees, query]);

  const selectedSet = useMemo(() => new Set(selectedCodes), [selectedCodes]);
  const selectedEmployees = useMemo(
    () => employees.filter((e) => selectedSet.has(e.employee_code)),
    [employees, selectedSet],
  );

  function toggle(code: string) {
    if (selectedSet.has(code)) onChange(selectedCodes.filter((c) => c !== code));
    else onChange([...selectedCodes, code]);
  }

  return (
    <div ref={rootRef} className={`relative ${className}`}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex items-center justify-between gap-2 w-full bg-surface-muted border border-border rounded-xl text-sm text-foreground font-inter px-3 py-2 hover:border-muted-foreground transition-colors"
      >
        <span className={`truncate text-left ${selectedCodes.length === 0 ? "text-muted-foreground" : ""}`}>
          {selectedCodes.length > 0 ? `${selectedCodes.length} employee${selectedCodes.length > 1 ? "s" : ""} selected` : placeholder}
        </span>
        <ChevronDown className={`w-4 h-4 text-muted-foreground shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="absolute z-20 mt-1 w-56 bg-surface-muted border border-border rounded-xl shadow-2xl overflow-hidden">
          <div className="relative p-2 border-b border-border">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Name or employee code"
              className="w-full pl-7 pr-2 py-1.5 bg-surface border border-border rounded-lg text-sm text-foreground font-inter placeholder-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary/30"
            />
          </div>
          <div className="max-h-56 overflow-y-auto py-1">
            {filtered.length === 0 ? (
              <p className="px-3 py-2 text-xs text-muted-foreground font-inter">No employees found.</p>
            ) : (
              filtered.map((e) => {
                const checked = selectedSet.has(e.employee_code);
                return (
                  <label
                    key={e.id}
                    className="flex items-center gap-2 px-3 py-2 text-sm text-foreground font-inter hover:bg-surface cursor-pointer"
                  >
                    <input type="checkbox" className="hidden" checked={checked} onChange={() => toggle(e.employee_code)} />
                    <span
                      className={`flex items-center justify-center w-4 h-4 rounded border shrink-0 ${
                        checked ? "bg-primary border-primary" : "border-border"
                      }`}
                    >
                      {checked && <Check className="w-3 h-3 text-primary-foreground" />}
                    </span>
                    <span className="truncate">{e.name}</span>
                    <span className="ml-auto text-xs text-muted-foreground font-mono shrink-0">{e.employee_code}</span>
                  </label>
                );
              })
            )}
          </div>
          {selectedCodes.length > 0 && (
            <div className="flex items-center justify-between gap-2 border-t border-border px-3 py-2">
              <span className="text-xs text-muted-foreground font-inter">{selectedCodes.length} selected</span>
              <button
                type="button"
                onClick={() => onChange([])}
                className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground font-inter"
              >
                <X className="w-3 h-3" /> Clear
              </button>
            </div>
          )}
        </div>
      )}

      {selectedEmployees.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-1.5">
          {selectedEmployees.map((e) => (
            <span
              key={e.id}
              className="inline-flex items-center gap-1 px-2 py-0.5 bg-surface-muted border border-border rounded-full text-xs text-foreground font-inter"
            >
              {e.name}
              <button type="button" onClick={() => toggle(e.employee_code)} className="text-muted-foreground hover:text-foreground">
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
